<template>
  <div>
    <loader :isLoading="isRequestInProgress" />

    <section v-if="!isRequestInProgress" class="public-view-landing-content pass-view-landing-container">
      <!-- if not a valid pass load 404 page -->
      <div v-if="!isValidPass">
        <h3 class="text-center">This is not a valid pass</h3>
        <Page404 :image="load404Image"/>
      </div>
      <!-- end load 404 page -->

      <!-- there's no loggedIn user -->
      <not-logged-in
        :isToVerifyIdentity="isPassToVerifyIdentity"
        :isUserInvited="isUserInvited"
        :companyName="companyName"
        :companyLogoUrl="companyLogoUrl"
        :requestAccess="passDetail.request_access"
        :passIntro="passDetail.pass_intro"
        :passName="passDetail.pass_name"
        :inviteToken="inviteToken"
        :originUrl="originUrl"
        :isLoggedIn="isLoggedIn"
        :isValidPass="isValidPass"
        :isRequestAccessAllowed="isRequestAccessAllowed"
        :isRequestNeedsApproval="isRequestNeedsApproval"
        :load404Message="load404Message" />
      <!-- end no loggedIn user -->

      <!-- if loggedIn -->
      <div v-if="isLoggedIn && isValidPass && !load404 && !isBusinessAccount">
        <!-- pass request access allowed or user is invited -->
        <div v-if="isRequestAccessAllowed || isUserInvited">
          <!-- if user already accepted the pass -->
          <div v-if="reviewAgreementOk && shareDetailsOk">
            <div class="d-flex flex-row justify-content-center"
              v-if="passDetail.status === 'Pending' && isUserInvited">
              <p class="pass-access-notice">Yey! You can now access this pass.</p>
            </div>

            <div class="d-flex flex-row justify-content-center" v-if="!isPassStillActive">
              <p class="pass-access-notice" v-if="passDetail.status === 'Expired'">
                This pass has {{ passDetail.status }}.
              </p>
              <p class="pass-access-notice" v-else-if="passDetail.status === 'Deleted'">
                This pass has been {{ passDetail.status }}.
              </p>
              <p class="pass-access-notice" v-else>
                This pass is {{ passDetail.status }}.
              </p>

              <!-- add agreements view and PII will be dispose when the pass is disposed -->
              
            </div>

            <div class="d-flex flex-row justify-content-center">
              <div>
                <div v-if="individualPassDetail.pass_status === 'Waiting for approval'">
                  <LandingHeader
                    :isToVerifyIdentity="isPassToVerifyIdentity"
                    :isUserInvited="isUserInvited"
                    :isLoggedIn="isLoggedIn"
                    :isRequestAccepted="isRequestAccepted"
                    :companyName="companyName"
                    :companyLogo="companyLogoUrl"
                    :requestAccess="passDetail.request_access"
                    :passIntro="passDetail.pass_intro"
                    :passName="passDetail.pass_name" />

                  <div class="text-center waiting-for-approval">
                    <img class="checkmark" :src="greenCheckImage" width="40" height="40">
                    <p>Thank you for requesting pass.</p>
                    <span>Your request is now <b>waiting for approval</b>.<br>
                    We'll send you an email at <b>{{ loggedInUserDetail.email_address }}</b>
                    once admin has taken action.</span>
                  </div>
                </div>
                <div class="pass-access-notice"
                  v-if="individualPassDetail.pass_status === 'Pass Request Rejected' && isRequestAccessAllowed">
                  {{ individualPassDetail.pass_status }}
                </div>
              </div>
            </div>

            <PassShareViewDetails v-if="individualPassDetail.pass_status === 'Active'"
              :companyLogo="companyLogoUrl"
              :companyName="companyName"
              :passDetail="passDetail"
              :hasQR="hasQR"
              :isToVerifyIdentity="isPassToVerifyIdentity"
              :qrcodeValue="qrcodeValue"/>
          </div>
          <!-- end if user already accepted the pass-->

          <!-- if user haven't accepted the pass -->
          <div v-else>
            <LandingHeader
              :isToVerifyIdentity="isPassToVerifyIdentity"
              :isUserInvited="isUserInvited"
              :isLoggedIn="isLoggedIn"
              :isRequestAccessAllowed="isRequestAccessAllowed"
              :isRequestAccepted="isRequestAccepted"
              :companyName="companyName"
              :companyLogo="companyLogoUrl"
              :requestAccess="passDetail.request_access"
              :passName="passDetail.pass_name"
              :passIntro="passDetail.pass_intro"/>

            <steps-view
              :steps="getSteps"
              :passDetail="passDetail"
              :isToVerifyIdentity="isPassToVerifyIdentity"
              :isUserInvited="isUserInvited"
              :isRequestNeedsApproval="isRequestNeedsApproval"
              :isUserVerified="isUserVerified"
              :loggedInUserDetail="loggedInUserDetail"
              :loggedInPerson="loggedInPerson"
              :reviewAgreementQuestion="reviewAgreementQuestion"
              :reviewAgreementOk="reviewAgreementOk"
              :updateIndiPassReviewAgreementOk="updateIndiPassReviewAgreementOk"
              :userDetailsQuestion="userDetailsQuestion"
              :updateShareDetailsIndividualPass="updateShareDetailsIndividualPass"
              :activeStep="getActiveStep"
              :originUrl="originUrl"
              :individualPassDetail="individualPassDetail"
              :passQuestion="passQuestion"/>
          </div>
          <!-- if user haven't accepted the pass -->
        </div>
        <!-- end pass request access allowed or user is invited-->
      </div>
      <!-- end if logged in-->

      <!-- load 404 if no access to the  pass -->
      <div v-if="load404">
        <Page404 :message="load404Message" :image="load404Image"/>
      </div>
      <!-- end load 404 -->

      <!-- load 404 if no access to the  pass -->
      <div v-if="isBusinessAccount">
        <Page404 :message="loadBusinessErrorMessage" :image="load404Image"/>
      </div>
      <!-- end load 404 -->

      <div class="pass-step-footer">
        <p>
          Have problems? Email us at
          <a href="mailto:team@yeypass.com">team@yeypass.com</a>
        </p>
      </div>
    </section>
  </div>
</template>

<script>
import '@/assets/styles/pass-share-steps.css'

import Loader from '@/components/common/Loader'
import Page404 from '@/components/Page404'

import LandingHeader from '@/components/pass_share/LandingHeader'
import NotLoggedIn from './NotLoggedIn'
import PassShareStep from './PassShareSteps'
import PassShareViewDetails from './PassShareViewDetails'
import StepsView from './StepsView'

import { APIService } from '@/api/APIService'
import {
  passQuestion,
  reviewAgreementQuestion,
  userDetailsQuestion
} from '@/api/constants/Passes'
import { DateTimeHelper } from '@/api/helper/DateTimeHelper'
import { LINK_INVALID, GREEN_CHECK } from '@/api/constants/ImageAssets'

const api = new APIService()
const dateHelper = new DateTimeHelper()

export default {
  components: {
    Loader,
    Page404,
    LandingHeader,
    NotLoggedIn,
    PassShareStep,
    PassShareViewDetails,
    StepsView
  },
  data() {
    return {
      originUrl: '',
      companyLogoUrl: '',
      companyName: '',
      qrcodeValue: '',
      load404Image: LINK_INVALID,
      greenCheckImage: GREEN_CHECK,
      passQuestion: passQuestion(this.linkId),
      reviewAgreementQuestion: reviewAgreementQuestion(this.linkId),
      userDetailsQuestion: userDetailsQuestion(this.linkId),
      loadBusinessErrorMessage: 'Business account cannot request pass,\nplease create your individual pass',
      load404Message: 'You don\'t have permission to access this pass,\nmessage company who send you this link if this was a mistake.',
      loggedInUserDetail: {},
      passDetail: {},
      individualPassDetail: {},
      passInviteParams: {},
      passInvitesList: [],
      passAccessQuestionResponse: [],
      isValidPass: false,
      isRequestInProgress: true,
      isPassToVerifyIdentity: false,
      isPassStillValid: true,
      isUserInvited: false,
      isUserVerified: false,
      isToProceed: false,
      load404: false,
      hasInvites: false,
      hasQR: false,
      reviewAgreementOk: false,
      shareDetailsOk: false,
      isRequestAccessAllowed: false,
      isRequestAccepted: false,
      isRequestNeedsApproval: false,
      isBusinessAccount: false
    }
  },
  props: {
    accountType: String,
    userId: String,
    linkId: String,
    loggedInPerson: String,
    inviteToken: String,
    isLoggedIn: Boolean,
    goToLogin: Function,
    goToVerifyAccount: Function
  },
  computed: {
    // IMPORTANT: Array item values must be the same with id values on 'PassShareStep' `source` array
    getSteps() {
      if (this.isPassToVerifyIdentity) return ['verify', 'review', 'share']
      else return ['review', 'share']
    },
    getActiveStep() {
      // This value is passed over to PassShareStep which identifies
      // if which step will show its contents or only the preview.
      if (this.isPassToVerifyIdentity && !this.isUserVerified) return 'verify'
      if (!this.reviewAgreementOk) return 'review'
      if (this.reviewAgreementOk) return 'share'
    },
    isPassStillActive() {
      const status = this.passDetail.status
      if(status === 'Expired' || status === 'Deactivated' || status === 'Deleted') return false
      else return true
    },
    haveInviteToken() {
      return this.inviteToken !== '' && this.inviteToken !== undefined
    }
  },
  mounted() {
    /*
    TODO - userflow:
    1. get sharedPassDetails by link id
    2. check passDetails
    - if user not logged in, then check if pass is valid and not expired
    - if user is logged in
      - check if its the owner, redirect to owner pass detail
      - else
        - get loggedInUser details
        - get passDetails invites and requests
        - then check if loggedIn user have pass access
    */
    this.originUrl = `${window.location.origin}/invite/passes/${this.linkId}`
    this.getPassDetailByShareableLinkId()
  },
  methods: {
    // async functions
    async getPassDetailByShareableLinkId() {
      try {
        this.isRequestInProgress = true
        const pass = await api.getUserShareablePasses(this.linkId)
        if(Object.keys(pass).length === 0 || pass.statusCode === 400) {
          this.isValidPass = false
          this.isRequestInProgress = false
        } else {
          this.passDetail = pass
          this.isValidPass = true
          this.checkPassDetails()
        }
      } catch(e) {
        this.isValidPass = false
        this.isRequestInProgress = false
      }
    },
    async getLoggedInUserDetails() {
      this.isRequestInProgress = true
      const user = await api.getUserDetails(this.userId)
      this.loggedInUserDetail = user
    },
    async getInvitesAndRequests() {
      try {
        this.isRequestInProgress = true
        const pass = await api.getPassInviteRequestsByLink(this.linkId)
        if(Object.keys(pass).length !== 0) {
          if(pass.invites_data.length !== 0) {
            this.passInvitesList = pass.invites_data
            this.hasInvites = true
          }

          if(pass.requests_data.length !== 0) {
            this.isRequestAccepted = pass.requests_data.filter(e =>
              e.requestor_user_id === this.userId && e.request_status === 'Accepted'
            ).length !== 0
          }
          this.checkLoggedInUserHasPassAccess()
        } else {
          this.isValidPass = false
          this.isRequestInProgress = false
        }
      } catch(e) {
        this.isValidPass = false
        this.isRequestInProgress = false
      }
    },
    async getIndividualPassDetail() {
      this.isRequestInProgress = true
      this.individualPassDetail = await api.getIndividualPassWithLinkID(this.userId, this.linkId)
      this.checkIndividualPassDetail()
    },
    async createOwnerPassRequest(status) {
      this.isRequestInProgress = true
      const params = {
        pass_id: this.passDetail.id,
        owner_id: this.passDetail.owner_id,
        requestor_user_id: this.userId,
        pass_contact_email: this.passDetail.company_email,
        request_needs_approval: this.passDetail.request_needs_approval,
        pass_access_questions_response: this.passAccessQuestionResponse
      }
      await api.createBusinessRequests(params)
      // todo: send email to company about pass request
      this.isRequestInProgress = false
    },
    async createIndividualPass() {
      this.isRequestInProgress = true
      const isRequestStatus = this.isRequestNeedsApproval ? 'Waiting for approval' : 'Active'

      const params = {
        pass_owner_id: this.passDetail.owner_id,
        pass_id: this.passDetail.id,
        reference_link_id: this.linkId,
        user_id: this.userId,
        accept_pass_ok: true,
        reviewed_agreement_ok: !this.isUserInvited,
        share_details_ok: !this.isUserInvited,
        pass_status: this.isUserInvited ? 'Pending' : isRequestStatus
      }
      await api.createIndividualPasses(params)
      this.isRequestInProgress = false
      this.getIndividualPassDetail()
    },
    async createUpdateOwnerPassInvite(params) {
      this.isRequestInProgress = true
      await api.createUpdatePassInvites(params)
      this.isRequestInProgress = false
    },
    async updateOwnerPassStatus() {
      this.isRequestInProgress = true
      const params = { status: 'Active' }
      await api.updatePassStatus(this.passDetail.id, params)
      this.isRequestInProgress = false
    },
    async updateIndiPassReviewAgreementOk() {
      if(this.isRequestAccessAllowed && !this.isUserInvited) {
        this.reviewAgreementOk = true
      } else {
        const params = { reviewed_agreement_ok: true }
        await api.updateIndividualPassReview(this.individualPassDetail.id, params)
        this.reviewAgreementOk = true
      }
    },
    async updateIndiPassShareDetailsOk() {
      this.isRequestInProgress = true
      if(this.isUserInvited) {
        const params = { share_details_ok: true, pass_status: 'Active' }
        const shareOk = await api.updateIndividualPassShareDetails(this.individualPassDetail.id, params)
        this.shareDetailsOk = shareOk.statusCode === 200
        await this.getIndividualPassDetail()
        this.isRequestInProgress = false
      } else {
        this.createIndividualPass()
      }
      if(this.isUserInvited) this.constructOwnerPassInvite()
      else if(!this.isUserInvited && this.isRequestAccessAllowed) this.createOwnerPassRequest(status)
    },
    async updateUserProfileFromSharedDetails(params) {
      this.isRequestInProgress = true
      const fields = { profile_details: { ...params } }
      await api.updateProfile(this.userId, fields)
      this.isRequestInProgress = false
      this.updateIndiPassShareDetailsOk()
    },

    // non-async methods
    checkPassDetails() {
      this.isPassToVerifyIdentity = this.passDetail.verify_identity
      this.companyName = this.passDetail.company_name
      this.companyLogoUrl = this.passDetail.company_logo_url

      const passValidUntil = this.passDetail.valid_until
      const epochTimeToday = dateHelper.getEpochTimeToday()
      const isPassStillActive = this.passDetail.status === 'Active' || this.passDetail.status === 'Pending'
      this.isPassStillValid = (epochTimeToday < passValidUntil) && isPassStillActive

      this.isRequestAccessAllowed = this.passDetail.request_access
      this.isRequestNeedsApproval = this.passDetail.request_needs_approval

      if(this.isLoggedIn) {
        if(this.userId === this.passDetail.owner_id) {
					this.$router.push(`/passes/${this.passDetail.id}`)
				} else {
          // console.log('getloggedin and invites details')
          this.getLoggedInUserDetails()
          this.getInvitesAndRequests()
				}
      } else {
        this.isRequestInProgress = false
        if(!this.isRequestAccessAllowed && !this.haveInviteToken) {
          this.load404 = true
        }
      }
    },
    checkLoggedInUserHasPassAccess() {
      // console.log('checkLoggedInUserHasPassAccess')
      const loggedInAccountType = this.loggedInUserDetail.account_type
      if(loggedInAccountType === 'business') {
        this.isBusinessAccount = true
        this.isRequestInProgress = false
        return
      }

      this.isUserVerified = this.loggedInUserDetail.is_account_verified
      if(this.hasInvites) {
        // console.log('hasInvites')
        this.userEmail = this.loggedInUserDetail.email_address
        this.isUserInvited = this.passInvitesList.filter(e => {
          return e.email.includes(this.userEmail) }).length !== 0
        this.load404 = false

        // console.log('isUserInvited: ', this.isUserInvited)
        if(!this.isUserInvited) this.checkIfPassHaveRequestAccess()
        else {
          this.load404 = false
          this.getIndividualPassDetail()
        }
      } else {
        // console.log('not invited')
        this.isUserInvited = false
        this.checkIfPassHaveRequestAccess()
      }
    },
    checkIfPassHaveRequestAccess() {
      if(this.isRequestAccessAllowed) this.getIndividualPassDetail()
      else {
        this.load404 = true
        this.isRequestInProgress = false
      }
    },
    checkIndividualPassDetail() {
      // console.log('checkIndividualPassDetail: ', this.individualPassDetail)
      if(Object.keys(this.individualPassDetail).length !== 0) {
        // console.log('exists indi pass')
        this.hasQR = true
        this.reviewAgreementOk = this.individualPassDetail.reviewed_agreement_ok
        this.shareDetailsOk = this.individualPassDetail.share_details_ok
        this.qrcodeValue = this.individualPassDetail.qrcode_value
        this.isRequestInProgress = false
      } else {
        // console.log('not exists indi pass')
        if(this.isPassStillValid) {
          // console.log('exists indipass still valid')
          if(this.isUserInvited) this.createIndividualPass()
          else {
            // console.log('proceed')
            this.isRequestInProgress = false
          }
        } else {
          // console.log('indipass expired')
          this.load404 = true
          this.load404Message = 'This pass is already expired. Request for a new one instead.'
          this.isRequestInProgress = false
        }
      }
    },
    updateShareDetailsIndividualPass(params) {
      // update profile details from included_pass_details as requested by pass owner
      if(Object.keys(params.user_details).length === 0 &&
        Object.keys(params.pass_access_questions).length === 0) {
        this.updateIndiPassShareDetailsOk()
      } else {
        this.updateUserProfileFromSharedDetails(params.user_details)
        this.passAccessQuestionResponse = params.pass_access_questions_response
      }
    },
    constructOwnerPassInvite() {
      this.passInviteParams = {
        pass_id: this.passDetail.id,
        user_id: this.passDetail.owner_id,
        invites: [{
          email: this.loggedInUserDetail.email_address,
          user_id: this.userId,
          status: 'Accepted',
          pass_access_questions_response: this.passAccessQuestionResponse
        }]
      }
      this.createUpdateOwnerPassInvite(this.passInviteParams)
      this.updateOwnerPassStatus()
    }
  }
}

</script>

<style type="text/css">
  .pass-step-footer {
    padding-bottom: 30px;
  }
</style>

<style scoped>
  .waiting-for-approval {
    padding-top: 30px;
  }

  .checkmark {
    margin-bottom: 20px;
  }
</style>



// WEBPACK FOOTER //
// src/containers/share/passes/BasePassShare.vue